/**
 * Trim filters: remove empty-string values
 * @param {object} filters - The filters object, ex: {name:'', status: ['CREATED','ACTIVATED'], type:[]}
 * @returns {object} - The trimed filters object, ex: {status: ['CREATED','ACTIVATED'], type:[]}
 */
export function trimFilters(filters) {
  const nonEmptyFilters = {};
  Object.keys(filters).forEach((key) => {
    if (filters[key] !== '' && filters[key] !== null) {
      nonEmptyFilters[key] = filters[key];
    }
  });
  return nonEmptyFilters;
}

/**
 * Serialize Filters object, to be used as query string parameters
 * @param {object} filters - The filters object, ex: {status: ['CREATED','ACTIVATED'], product_type:['PHOTO']}
 * @return {string} - The serialized urlized filters, ex: `filter[product_type]=PHOTO&filter[status]=CREATED%2CACTIVATED`
 */
export function serializeFilters(filters) {
  const serialized = [];
  Object.keys(filters)
    .sort()
    .forEach((key) => serialized.push(
      `filter[${encodeURIComponent(key)}]=${encodeURIComponent(filters[key])}`,
    ));
  return serialized.join('&');
}

/**
 * Serialize query string parameters, Filters object.
 * @param queryString {string} - The serialized urlized filters, ex: `filter[product_type]=PHOTO&filter[status]=CREATED%2CACTIVATED`
 * @return {object} filters - The filters object, ex: {status: ['CREATED','ACTIVATED'], product_type:['PHOTO']}
 */
export function deserializeFilters({ queryString, onlyFilters = false, rename = true }) {
  let queryTuples = [];
  if (queryString) {
    const urlSearchParams = new URLSearchParams(queryString);
    queryTuples = [...urlSearchParams]
      .filter(([k]) => (onlyFilters && k.startsWith('filter[')) || !onlyFilters)
      .map(([k, v]) => {
        const isFilter = k.includes('filter');
        const key = rename ? k.replace(/filter\[|\]+/g, '') : k;
        let value = !isFilter ? v : v.split(',').map((a) => a.trim());
        value = isFilter && !Array.isArray(value) ? [value] : value;
        return [key, value];
      });
  }
  return Object.fromEntries(queryTuples);
}
/**
 * Merge filter parameters
 * { blocker: 'hello',  } + { blocker: 'world', someparam: true } = filter: { blocker: 'hello,world', someparam: true }
 *
 * @export
 * @class MergedQueryParameters
 */
export class MergedQueryParameters {
  overwrite = false;

  constructor(initialParams = {}) {
    this.internalParams = initialParams;
  }

  addParams(params) {
    Object.keys(params).forEach((paramKey) => {
      this.internalParams[paramKey] = params[paramKey];
    });
  }

  addFilter(filter) {
    if (!this.internalParams.filter) this.internalParams.filter = {};

    Object.keys(filter).forEach((filterKey) => {
      if (!this.internalParams.filter[filterKey]) {
        // if the filter is not already set, set it
        this.internalParams.filter[filterKey] = filter[filterKey];
      } else if (typeof this.internalParams.filter[filterKey] === 'string') {
        if (this.overwrite) {
          // if the filter is already set and overwrite is true, set it
          this.internalParams.filter[filterKey] = filter[filterKey];
        } else {
          // if the filter is already set, add it to the existing value
          this.internalParams.filter[filterKey] = this.internalParams
          .filter[filterKey].concat(',', filter[filterKey]);
        }
      } else {
        throw new Error(`Filter ${filterKey} is not a string`);
      }
    });
  }

  get params() {
    return this.internalParams;
  }
}
